// Item Setup: is an item ready to build and buy? One task list per item: the master data baseline checked in BC
// (each check passes on its own once BC is right), the item's PLM change order, and department sign-offs, each
// with an owner and a due date counted back from the contract's first delivery.
import { Fragment, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, type ItemSetup, type SetupTask } from "../api";
import { useLoad } from "../hooks";
import { CommandBar } from "../Shell";
import { ErrorBox, ago } from "../ui";
import { XSection } from "../crm";
import { bcUrl } from "../config";
import { ActionBar, RecordHeader, type Act, type Fact } from "../record";

const usDate = (d: string | null | undefined) => (d ? new Date(d.length === 10 ? d + "T00:00:00" : d).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }) : "");
const todayIso = () => new Date().toISOString().slice(0, 10);
const ACT = (p: string) => <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d={p} fill="none" stroke="currentColor" strokeWidth="1.4" /></svg>;
const ICON = { sync: ACT("M13 5A5.5 5.5 0 0 0 3 6M3 11a5.5 5.5 0 0 0 10-1M13 2v3h-3M3 14v-3h3"), ok: ACT("M3 8.5l3 3 7-7"), cad: ACT("M2.5 2.5h11v11h-11zM5 11l3-6 3 6") };
const bcItemUrl = (no: string) => `${bcUrl(30)}&filter=${encodeURIComponent(`'No.' IS '${no}'`)}`;
export const SETUP_STATE: Record<string, string> = { open: "Open", ready: "Ready", cancelled: "Cancelled" };
const TASK_STATE: Record<string, string> = { open: "Open", done: "Done", failing: "Failing", waived: "Waived", na: "Not Applicable" };
const KIND: Record<string, string> = { check: "BC check", link: "Change order", signoff: "Sign-off" };
const isDone = (t: SetupTask) => ["done", "waived", "na"].includes(t.state);

/* ---------- List ---------- */

export function ItemSetupsPage() {
  const [params, setParams] = useSearchParams();
  const view = params.get("view") ?? "";
  const { data, error } = useLoad(() => api.itemSetups(view || undefined), [view]);
  const rows = data ?? [];
  const late = rows.filter((r) => r.state === "open" && r.readyBy && r.readyBy < todayIso());
  const set = (v: string | null) => { const p = new URLSearchParams(params); v ? p.set("view", v) : p.delete("view"); setParams(p, { replace: true }); };
  return (
    <>
      <CommandBar status={data ? `${rows.length} ${rows.length === 1 ? "setup" : "setups"}` : ""}>
        <Link className="cmd" to="/contracts">Start from a Contract Line</Link>
      </CommandBar>
      <div className="page card-page">
        <div className="rec-title"><h1>Item Setups</h1><span className="meta">Every item a contract sells is checked against BC's master data. A setup carries the work for one that isn't ready yet.</span></div>
        <ErrorBox error={error} />
        <div className="cues">
          <div className="cue"><span className="cue-l">Open</span><span className="cue-v">{rows.filter((r) => r.state === "open").length}</span></div>
          <div className="cue"><span className="cue-l">Past Ready By</span><span className="cue-v">{late.length}</span></div>
          <div className="cue"><span className="cue-l">Failing BC Checks</span><span className="cue-v">{rows.reduce((t, r) => t + r.failing, 0)}</span></div>
        </div>
        <div className="panel">
          <div role="tablist" aria-label="Views" className="views">
            {[["", "Not Cancelled"], ["open", "Open"], ["ready", "Ready"], ["all", "All"]].map(([id, name]) => (
              <button key={id} role="tab" aria-selected={view === id} className={view === id ? "on" : ""} onClick={() => set(id || null)}>{name}</button>
            ))}
          </div>
          {!data ? <div className="empty">Loading…</div> : !rows.length ? (
            <div className="empty">No item setups in this view. Open a contract: its Items section shows which items aren't ready, with Start Setup beside each.</div>
          ) : (
            <div className="table-wrap">
              <table className="dgrid compact num-grid">
                <thead><tr><th>No.</th><th>Item</th><th>Description</th><th>Make or Buy</th><th>Contract</th><th>Ready By</th><th className="r">Open Tasks</th><th>Next Task</th><th>Owner</th><th>Status</th><th>Updated</th></tr></thead>
                <tbody>{rows.map((r) => {
                  const isLate = r.state === "open" && !!r.readyBy && r.readyBy < todayIso();
                  return (
                    <tr key={r.id}>
                      <td><Link to={`/item-setups/${r.number}`}>{r.number}</Link></td>
                      <td>{r.itemNumber}</td>
                      <td>{r.title.replace(new RegExp(`^${r.itemNumber.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*`), "")}</td>
                      <td>{r.makeOrBuy === "make" ? "Make" : "Buy"}</td>
                      <td>{r.contractNumber ? <Link to={`/contracts/${r.contractNumber}`}>{r.contractNumber}</Link> : ""}</td>
                      <td className={isLate ? "bad" : ""}>{usDate(r.readyBy)}{isLate ? ", late" : ""}</td>
                      <td className="r">{r.state === "cancelled" ? "" : `${r.open} of ${r.total}`}{r.failing ? <span className="bad">, {r.failing} failing</span> : null}</td>
                      <td>{r.state === "open" ? r.nextTask ?? "" : ""}</td>
                      <td>{r.state === "open" ? r.nextOwner ?? "" : ""}</td>
                      <td>{SETUP_STATE[r.state] ?? r.state}</td>
                      <td className="muted">{ago(r.updatedAt)}</td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/* ---------- Record ---------- */

export function ItemSetupPage() {
  const { id = "" } = useParams();
  const { data, error, reload } = useLoad(() => api.itemSetup(id), [id]);
  const [over, setOver] = useState<ItemSetup | null>(null);
  const s = over && data && over.id === data.id && over.updatedAt >= data.updatedAt ? over : data;
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<unknown>(null);
  const [waiving, setWaiving] = useState<string | null>(null);
  const [reason, setReason] = useState("");

  const run = async (fn: () => Promise<ItemSetup>) => {
    setBusy(true); setActionError(null);
    try { setOver(await fn()); } catch (err) { setActionError(err); setOver(null); await reload(); } finally { setBusy(false); }
  };

  if (error) return <div className="page"><ErrorBox error={error} /></div>;
  if (!s) return <div className="page"><div className="panel empty">Loading…</div></div>;

  const live = s.state !== "cancelled";
  const failing = s.tasks.filter((t) => t.state === "failing");
  const checksOpen = s.tasks.filter((t) => t.kind === "check" && !isDone(t));
  const nextSignoff = s.tasks.filter((t) => t.kind === "signoff" && t.required && !isDone(t)).sort((a, b) => (a.dueDate ?? "9999").localeCompare(b.dueDate ?? "9999"))[0];
  const late = s.state === "open" && !!s.readyBy && s.readyBy < todayIso();
  const check: Act = { label: "Check Business Central", icon: ICON.sync, onClick: () => run(() => api.checkItemSetup(s.id)), disabled: busy };
  const signOff = (t: SetupTask): Act => ({ label: `Sign Off: ${t.stage}`, icon: ICON.ok, onClick: () => run(() => api.setupTask(s.id, t.key, { action: "done" })), disabled: busy });
  const coAct: Act = s.changeOrder
    ? { label: `Open ${s.changeOrder.number}`, to: `/plm/changes/${s.changeOrder.number}` }
    : { label: "Import Structure from CAD", icon: ICON.cad, to: `/plm/import?part=${encodeURIComponent(s.itemNumber)}&description=${encodeURIComponent(s.title.replace(s.itemNumber, "").trim())}` };
  const primary: Act | null = !live || s.state === "ready" ? null : checksOpen.length ? check : nextSignoff ? signOff(nextSignoff) : check;

  const next = !live ? null
    : s.state === "ready" ? { tone: "done" as const, text: <>Every required task is done{s.readyAt ? ` (ready since ${usDate(s.readyAt)})` : ""}. The contract can release sales orders for {s.itemNumber}.</> }
    : failing.length ? { tone: "warn" as const, text: <>{failing.length} BC {failing.length === 1 ? "check is" : "checks are"} failing: {failing.map((t) => t.title.toLowerCase()).join(", ")}. Fix {failing.length === 1 ? "it" : "them"} in Business Central, then choose <strong>Check Business Central</strong>.</> }
    : checksOpen.length ? { text: <>{checksOpen.length} BC {checksOpen.length === 1 ? "check hasn't" : "checks haven't"} run yet. Choose <strong>Check Business Central</strong>.</> }
    : nextSignoff ? { tone: nextSignoff.late ? ("warn" as const) : ("info" as const), text: <>{nextSignoff.department}: {nextSignoff.title.charAt(0).toLowerCase() + nextSignoff.title.slice(1)}{nextSignoff.dueDate ? <>, due <strong>{usDate(nextSignoff.dueDate)}</strong></> : null}. Choose <strong>Sign Off: {nextSignoff.stage}</strong> when it's done.</> }
    : null;

  const facts: Fact[] = [
    { label: "Item", value: <a href={bcItemUrl(s.itemNumber)} target="_blank" rel="noopener noreferrer">{s.itemNumber}</a>, note: s.makeOrBuy === "make" ? "Make" : "Buy" },
    { label: "Template", value: s.template.name, note: `Version ${s.template.version}` },
    { label: "Ready By", value: s.readyBy ? usDate(s.readyBy) : <span className="muted">No delivery date</span>, note: late ? "Late" : s.contract ? "First delivery less lead time" : undefined, tone: late ? "bad" : undefined },
    { label: s.contracts.length > 1 ? "Contracts" : "Contract", value: s.contracts.length ? <>{s.contracts.map((k, i) => <Fragment key={k.id}>{i ? ", " : ""}<Link to={`/contracts/${k.number}`}>{k.number}</Link></Fragment>)}</> : s.contract ? <Link to={`/contracts/${s.contract.number}`}>{s.contract.number}</Link> : <span className="muted">None</span> },
    { label: "Change Order", value: s.changeOrder ? <Link to={`/plm/changes/${s.changeOrder.number}`}>{s.changeOrder.number}</Link> : <span className="muted">None</span>, note: s.changeOrder ? s.changeOrder.state.replace("_", " ") : undefined },
  ];

  const action = (t: SetupTask) => {
    if (!live) return null;
    if (t.state === "done" && t.kind === "signoff") return <button className="linkish" disabled={busy} onClick={() => run(() => api.setupTask(s.id, t.key, { action: "reopen" }))}>Reopen</button>;
    if (t.state === "waived") return <button className="linkish" disabled={busy} onClick={() => run(() => api.setupTask(s.id, t.key, { action: "reopen" }))}>Reopen</button>;
    if (isDone(t)) return null;
    return <>
      {t.kind === "signoff" && <button className="linkish" disabled={busy} onClick={() => run(() => api.setupTask(s.id, t.key, { action: "done" }))}>Sign Off</button>}
      {t.kind === "signoff" && " · "}
      <button className="linkish" disabled={busy} onClick={() => { setWaiving(t.key); setReason(""); }}>Waive</button>
    </>;
  };

  return (
    <>
      <ActionBar
        primary={primary}
        steps={[{ ...check, hidden: !live || primary === check }, { ...coAct, hidden: !live }]}
        tools={[{ label: "Open Item in BC", href: bcItemUrl(s.itemNumber) }]}
        more={[{ label: "Cancel Setup", danger: true, hidden: !live, onClick: () => { const r = window.prompt(`Cancel ${s.number}? Why?`); if (r !== null) run(() => api.cancelItemSetup(s.id, r)); } }]}
        status={s.checkedAt ? `Checked in BC ${ago(s.checkedAt)}` : "Not checked in BC yet"}
      />
      <div className="page card-page">
        <RecordHeader
          crumb={{ to: "/item-setups", label: "Item Setups" }} kicker="Item Setup" number={s.number} title={s.title}
          status={{ label: SETUP_STATE[s.state] ?? s.state, tone: s.state === "ready" ? "ok" : "" }}
          facts={facts} next={next}
        />
        <ErrorBox error={actionError} />
        {s.checkError && <div className="notify warn" role="alert"><span>BC check didn't run: {s.checkError}</span></div>}

        <div className="cues">
          <div className="cue"><span className="cue-l">Required Tasks Done</span><span className="cue-v">{s.total - s.open} of {s.total}</span></div>
          <div className="cue"><span className="cue-l">Failing BC Checks</span><span className={`cue-v ${failing.length ? "bad" : ""}`}>{failing.length}</span></div>
          <div className="cue"><span className="cue-l">Next Task</span><span className="cue-v" style={{ fontSize: 15 }}>{s.next ? s.next.title : s.state === "ready" ? "None" : ""}</span>{s.next && <span className={`cue-n ${s.next.late ? "bad" : ""}`}>{s.next.department}{s.next.dueDate ? `, due ${usDate(s.next.dueDate)}` : ""}</span>}</div>
        </div>

        <div className="frame"><div className="tabs">
          <XSection title="Tasks" chips={[`${s.total - s.open} of ${s.total} done`, failing.length ? `${failing.length} failing` : null]}>
            <div className="table-wrap">
              <table className="dgrid compact">
                <thead><tr><th>Task</th><th>Kind</th><th>Owner</th><th>Due</th><th>Status</th><th>In BC / Notes</th><th aria-label="Actions" /></tr></thead>
                <tbody>{s.stages.map((stage) => (
                  <Fragment key={stage}>
                    <tr className="group-row"><td colSpan={7}>{stage}</td></tr>
                    {s.tasks.filter((t) => t.stage === stage).map((t) => (
                      <Fragment key={t.key}>
                        <tr>
                          <td>{t.title}{!t.required && <span className="muted"> (optional)</span>}</td>
                          <td className="muted">{KIND[t.kind]}</td>
                          <td>{t.assigneeName ?? t.department}</td>
                          <td className={t.late ? "bad" : ""}>{isDone(t) ? "" : usDate(t.dueDate)}</td>
                          <td className={t.state === "failing" || t.late ? "bad" : ""}>{t.late && t.state === "open" ? "Late" : TASK_STATE[t.state]}</td>
                          <td className="wrap">
                            {t.state === "waived" ? <>Waived by {t.doneBy}: {t.reason}</> : t.result ? <>{t.result.found}{t.result.fix && !isDone(t) ? <span className="fix"> {t.result.fix}</span> : null}</> : t.state === "done" ? <>Signed off by {t.doneBy}, {usDate(t.doneAt)}</> : ""}
                          </td>
                          <td className="r nowrap">{action(t)}</td>
                        </tr>
                        {waiving === t.key && (
                          <tr><td colSpan={7}>
                            <form className="sf-inline" onSubmit={(e) => { e.preventDefault(); if (!reason.trim()) return; run(async () => { const x = await api.setupTask(s.id, t.key, { action: "waive", reason }); setWaiving(null); return x; }); }}>
                              <label htmlFor={`w-${t.key}`}>Why waive "{t.title}"?</label>
                              <input id={`w-${t.key}`} className="input" style={{ minWidth: 280 }} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
                              <button className="btn primary" disabled={busy || !reason.trim()}>Waive</button>
                              <button type="button" className="btn" onClick={() => setWaiving(null)}>Cancel</button>
                            </form>
                          </td></tr>
                        )}
                      </Fragment>
                    ))}
                  </Fragment>
                ))}</tbody>
              </table>
            </div>
            <p className="xsec-note">BC checks pass on their own once Business Central is right; they can't be signed off by hand. The change order task finishes when {s.changeOrder ? s.changeOrder.number : "the item's change order"} is released.</p>
          </XSection>

          <XSection title="History" chips={[`${s.history.length} ${s.history.length === 1 ? "entry" : "entries"}`]} open={false}>
            <div className="table-wrap"><table className="dgrid compact">
              <thead><tr><th>When</th><th>Who</th><th>What</th></tr></thead>
              <tbody>{[...s.history].reverse().map((h, i) => (
                <tr key={i}><td className="nowrap">{new Date(h.at).toLocaleString("en-US")}</td><td>{h.actorName ?? ""}</td><td className="wrap">{h.fromState && h.fromState !== h.toState ? `${SETUP_STATE[h.fromState] ?? h.fromState} to ${SETUP_STATE[h.toState] ?? h.toState}. ` : !h.fromState ? "Started. " : ""}{h.reason ?? ""}</td></tr>
              ))}</tbody>
            </table></div>
          </XSection>
        </div></div>
      </div>
    </>
  );
}
